import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  Rating,
} from '@mui/material';
import {
  Assessment,
  Security,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Info,
  AttachMoney,
} from '@mui/icons-material';

import { CreditRiskAPI, formatCurrency, formatPercentage } from '../services/api';
import { InputCatalogues, LoanApplicationData, PredictionResult } from '../types';
import UnderwritingChat from './UnderwritingChat';

// Scorecard response interface that matches the API
interface ScorecardResponse {
  credit_score: number;
  score_band: string;
  default_probability: number;
  recommendation: string;
  max_loan_amount: number;
  suggested_rate_tier: string;
  odds: number;
  scorecard_factors: Array<{
    category: string;
    attribute: string;
    value: string | number;
    points: number;
    weight: number;
  }>;
  confidence_score: number;
  prediction_id: string;
  timestamp: string;
  input_warnings?: string[];
  max_loan_explanation?: string;
  band_cutoffs?: Record<string, number>;
  scored_at_reference_ticket?: boolean;
  requested_amount?: number;
  scoring_amount?: number;
  reference_ticket_note?: string;
}

// Fallback band cutoffs, used only if the catalogues request fails. Derived
// from the calibrated probability thresholds at the portfolio default rate,
// matching band_score_edges() on the server. The old hard-coded legend
// (A 750-850, B 700-749, ...) disagreed with the bands the model assigns, so a
// score of 594 was labelled band C while the legend highlighted D.
const FALLBACK_BAND_CUTOFFS: Record<string, number> = {
  A: 778,
  B: 675,
  C: 586,
  D: 503,
  E: 300,
};

const BAND_LABELS: Record<string, string> = {
  A: 'Excelente',
  B: 'Muy Bueno',
  C: 'Bueno',
  D: 'Regular',
  E: 'Malo',
};

const BAND_COLORS: Record<string, string> = {
  A: '#4caf50',
  B: '#8bc34a',
  C: '#ffc107',
  D: '#ff9800',
  E: '#f44336',
};

// The export stores values underscored and upper-cased, with accents already
// repaired by the API. Shown as-is they are the exact strings the model knows,
// which is what makes a result reproducible, so only the separators soften.
const prettyOption = (value: string): string => value.replace(/_/g, ' ');

interface UnifiedCreditAnalysisProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const UnifiedCreditAnalysis: React.FC<UnifiedCreditAnalysisProps> = ({ onNotification }) => {
  const [formData, setFormData] = useState<Partial<LoanApplicationData>>({
    // Personal Information
    Edad: 35,
    Genero: 'M',
    Estado_Civil: 'CASADO',

    // Loan Information
    Monto_Credito: 5000,
    Plazo: 36,
    Tasa_Fija: 18.5,
    Monto_Cuota: 185,

    // Financial Information. Activos/Pasivos/Capital are deliberately absent:
    // in the training export they run inverse to default (a blank balance
    // sheet marks an unfinished file, not a solvent borrower), so the models
    // hold them at portfolio constants and the form no longer collects them.
    Ingreso_Mensual: 2600,
    Ingresos_Anuales: 2600 * 12,

    // Business Information. Every value here exists in the model's trained
    // vocabulary; the menus are populated from /predict/catalogues at load.
    Producto: 'SUMA_PERSONAS',
    Destino_Credito: 'OTROS_DESTINOS',
    Sector_General: 'INDEPENDIENTE',
    Tipo_Persona: 'NATURAL',
    Tipo_Garantia: 'PRENDARIA',

    // Required by the second-generation scorecard. It has no branch for an
    // unknown category, so a value outside the catalogue is not ignored but
    // routed arbitrarily - hence selects rather than free text.
    Sector_Economico: 'TIENDAS_DE_AUTOSERVICIO',
    Sucursal_Grupo: '1-SEDE_PRINCIPAL',

    // Optional, and genuinely so: 19.9% of training rows carry no sales figure
    // and 1.9% no usable tenure, so the model learned a branch for their
    // absence. Leave them blank rather than guessing - a declared figure moved
    // one test applicant from a 23.2% default probability to 16.1%, and 0 is
    // read as 'not declared' rather than 'no revenue'.
    Importe_Venta: 16440,
    antiguedad_years: 10,

    // Location. Estado holds a province name in the export and is identical to
    // Provincia in every row, so the API derives it and the form omits it.
    Provincia: 'PANAMÁ',

    // Credit Information. The scorecard exists for clients with no credit
    // history, so NO_ASIGNADA is the honest value - the form used to assert
    // 'BUENO', and Clasificacion is an employment type, not a credit grade.
    Clasificacion: 'EMPLEADO',
    Califica_Credito: 'NO_ASIGNADA',
  });

  const [catalogues, setCatalogues] = useState<InputCatalogues | null>(null);

  const [loanDefaultPrediction, setLoanDefaultPrediction] = useState<PredictionResult | null>(null);
  const [scorecardData, setScorecardData] = useState<ScorecardResponse | null>(null);
  const [analysisKey, setAnalysisKey] = useState('');
  const [loading, setLoading] = useState(false);

  const calculateMonthlyPayment = (principal: number, rate: number, term: number): number => {
    if (rate === 0) return principal / term;
    const monthlyRate = rate / 100 / 12;
    return Math.round((principal * monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1));
  };

  const handleInputChange = (field: keyof LoanApplicationData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    const numericValue = ['Edad', 'Monto_Credito', 'Plazo', 'Tasa_Fija', 'Monto_Cuota', 'Ingresos_Anuales', 'Ingreso_Mensual', 'Importe_Venta', 'antiguedad_years'].includes(field)
      ? parseFloat(value) || 0
      : value;

    const updatedData = {
      ...formData,
      [field]: numericValue
    };

    // Auto-calculate monthly payment when loan amount, term, or rate changes
    if (['Monto_Credito', 'Plazo', 'Tasa_Fija'].includes(field)) {
      const principal = field === 'Monto_Credito' ? numericValue as number : (formData.Monto_Credito || 0);
      const rate = field === 'Tasa_Fija' ? numericValue as number : (formData.Tasa_Fija || 18.5);
      const term = field === 'Plazo' ? numericValue as number : (formData.Plazo || 36);

      updatedData.Monto_Cuota = calculateMonthlyPayment(principal, rate, term);
    }

    // Monthly income is what the branch actually collects; annual derives from it.
    if (field === 'Ingreso_Mensual') {
      updatedData.Ingresos_Anuales = Math.round((numericValue as number) * 12);
    }

    setFormData(updatedData);
  };

  useEffect(() => {
    let active = true;
    CreditRiskAPI.getInputCatalogues().then((response) => {
      if (active && response.success && response.data) {
        setCatalogues(response.data);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // Options for a field, from the model's own vocabulary. Falls back to
  // whatever is currently selected so a failed catalogues request leaves a
  // usable form rather than an empty menu.
  const optionsFor = (field: keyof LoanApplicationData): string[] => {
    const fromApi = catalogues?.fields?.[field as string]?.values;
    if (fromApi && fromApi.length) return fromApi;
    const current = formData[field];
    return current ? [String(current)] : [];
  };

  // Bands as score ranges, built from the cutoffs the API reports. A band's
  // range runs from its own cutoff to just below the next band up.
  const bandRanges = (cutoffs: Record<string, number>) => {
    const order = ['A', 'B', 'C', 'D', 'E'];
    return order.map((band, index) => {
      const min = cutoffs[band];
      const above = index === 0 ? 851 : cutoffs[order[index - 1]];
      return {
        band,
        min,
        max: above - 1,
        range: `${min}-${above - 1}`,
        label: BAND_LABELS[band],
        color: BAND_COLORS[band],
      };
    });
  };

  const handleSelectChange = (field: keyof LoanApplicationData) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleAnalyze = async () => {
    setLoading(true);
    setLoanDefaultPrediction(null);
    setScorecardData(null);
    setAnalysisKey('');

    try {
      // Call both APIs in parallel
      const payload = { ...formData, new_client: true } as LoanApplicationData;
      const [loanDefaultResponse, scorecardResponse] = await Promise.all([
        CreditRiskAPI.predictLoanDefault(payload),
        CreditRiskAPI.generateScorecard(payload as any)
      ]);

      if (loanDefaultResponse.success && loanDefaultResponse.data) {
        setLoanDefaultPrediction(loanDefaultResponse.data);
      }

      if (scorecardResponse.success && scorecardResponse.data) {
        setScorecardData(scorecardResponse.data as any);
      }

      if (loanDefaultResponse.success || scorecardResponse.success) {
        const nextKey = [
          loanDefaultResponse.data?.prediction_id,
          (scorecardResponse.data as any)?.prediction_id,
          Date.now(),
        ].filter(Boolean).join('-');
        setAnalysisKey(nextKey);
        onNotification('Credit analysis completed successfully!', 'success');
      } else {
        onNotification('Credit analysis failed', 'error');
      }
    } catch (error) {
      onNotification('Failed to connect to the server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setFormData({
      Edad: 35,
      Genero: 'M',
      Estado_Civil: 'CASADO',
      Monto_Credito: 5000,
      Plazo: 36,
      Tasa_Fija: 18.5,
      Monto_Cuota: 185,
      Ingreso_Mensual: 2600,
      Ingresos_Anuales: 2600 * 12,
      Producto: 'SUMA_PERSONAS',
      Destino_Credito: 'OTROS_DESTINOS',
      Sector_General: 'INDEPENDIENTE',
      Tipo_Persona: 'NATURAL',
      Tipo_Garantia: 'PRENDARIA',
      Sector_Economico: 'TIENDAS_DE_AUTOSERVICIO',
      Sucursal_Grupo: '1-SEDE_PRINCIPAL',
      Importe_Venta: 16440,
      antiguedad_years: 10,
      Provincia: 'PANAMÁ',
      Clasificacion: 'EMPLEADO',
      Califica_Credito: 'NO_ASIGNADA',
    });
    setLoanDefaultPrediction(null);
    setScorecardData(null);
    setAnalysisKey('');
  };

  const getRiskColor = (risk: number) => {
    if (risk < 0.2) return '#4caf50'; // Green
    if (risk < 0.5) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  const getScoreColor = (score: number) => {
    if (score >= 700) return '#4caf50'; // Green
    if (score >= 600) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  // Both models report the same category problems, so show each once.
  const inputWarnings = Array.from(
    new Set([
      ...(loanDefaultPrediction?.input_warnings || []),
      ...(scorecardData?.input_warnings || []),
    ])
  );

  const getDTIRatio = () => {
    if (!formData.Monto_Credito || !formData.Ingresos_Anuales) return 0;
    return (formData.Monto_Credito / formData.Ingresos_Anuales) * 100;
  };

  const getDebtServiceCoverageRatio = () => {
    if (!formData.Monto_Cuota || !formData.Ingreso_Mensual) return 0;
    return (formData.Monto_Cuota / formData.Ingreso_Mensual) * 100;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Análisis Crediticio Unificado
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Evaluación completa de riesgo crediticio y puntuación para nuevos solicitantes
      </Typography>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom color="primary">
              <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
              Información del Solicitante
            </Typography>

            {/* Personal Information */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="primary">
              Información Personal
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Edad"
                  type="number"
                  value={formData.Edad || ''}
                  onChange={handleInputChange('Edad')}
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Género</InputLabel>
                  <Select
                    value={formData.Genero || ''}
                    onChange={handleSelectChange('Genero')}
                    label="Género"
                  >
                    <MenuItem value="M">Masculino</MenuItem>
                    <MenuItem value="F">Femenino</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado Civil</InputLabel>
                  <Select
                    value={formData.Estado_Civil || ''}
                    onChange={handleSelectChange('Estado_Civil')}
                    label="Estado Civil"
                  >
                    <MenuItem value="SOLTERO">Soltero</MenuItem>
                    <MenuItem value="CASADO">Casado</MenuItem>
                    <MenuItem value="DIVORCIADO">Divorciado</MenuItem>
                    <MenuItem value="VIUDO">Viudo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Loan Information */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }} color="primary">
              Información del Préstamo
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Monto del Crédito (B/.)"
                  type="number"
                  value={formData.Monto_Credito || ''}
                  onChange={handleInputChange('Monto_Credito')}
                  size="small"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Plazo (meses)"
                  type="number"
                  value={formData.Plazo || ''}
                  onChange={handleInputChange('Plazo')}
                  size="small"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Tasa (%)"
                  type="number"
                  value={formData.Tasa_Fija || ''}
                  onChange={handleInputChange('Tasa_Fija')}
                  size="small"
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cuota Mensual (B/.) - Auto-calculada"
                  type="number"
                  value={formData.Monto_Cuota || ''}
                  InputProps={{ readOnly: true }}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                  helperText="Se calcula automáticamente"
                />
              </Grid>
            </Grid>

            {/* Financial Information */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }} color="primary">
              Información Financiera
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ingreso Mensual (B/.)"
                  type="number"
                  value={formData.Ingreso_Mensual || ''}
                  onChange={handleInputChange('Ingreso_Mensual')}
                  size="small"
                  helperText="Ganancia mensual neta (UEF), igual que en el expediente"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ingresos Anuales (B/.) - Auto-calculado"
                  type="number"
                  value={formData.Ingresos_Anuales || ''}
                  InputProps={{ readOnly: true }}
                  size="small"
                  helperText="Se calcula automáticamente como Ingreso Mensual × 12"
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Activos, Pasivos y Capital ya no se solicitan. En los datos
                  históricos estos campos van en sentido contrario al riesgo
                  (los solicitantes menos endeudados incumplieron 20.1% frente
                  a 7.2% de los más endeudados), porque un balance en blanco
                  indica un expediente sin completar y no un cliente solvente.
                  Los modelos los mantienen en constantes de cartera.
                </Alert>
              </Grid>
            </Grid>

            {/* Business Information */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }} color="primary">
              Información del Negocio
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Producto</InputLabel>
                  <Select
                    value={formData.Producto || ''}
                    onChange={handleSelectChange('Producto')}
                    label="Producto"
                  >
                    {optionsFor('Producto').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Destino del Crédito</InputLabel>
                  <Select
                    value={formData.Destino_Credito || ''}
                    onChange={handleSelectChange('Destino_Credito')}
                    label="Destino del Crédito"
                  >
                    {optionsFor('Destino_Credito').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Segmento (Sector General)</InputLabel>
                  <Select
                    value={formData.Sector_General || ''}
                    onChange={handleSelectChange('Sector_General')}
                    label="Segmento (Sector General)"
                  >
                    {optionsFor('Sector_General').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Tamaño de empresa, no actividad económica
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Persona</InputLabel>
                  <Select
                    value={formData.Tipo_Persona || ''}
                    onChange={handleSelectChange('Tipo_Persona')}
                    label="Tipo de Persona"
                  >
                    {optionsFor('Tipo_Persona').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provincia</InputLabel>
                  <Select
                    value={formData.Provincia || ''}
                    onChange={handleSelectChange('Provincia')}
                    label="Provincia"
                  >
                    {optionsFor('Provincia').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Empleo</InputLabel>
                  <Select
                    value={formData.Clasificacion || ''}
                    onChange={handleSelectChange('Clasificacion')}
                    label="Tipo de Empleo"
                  >
                    {optionsFor('Clasificacion').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Clasificacion en el expediente es tipo de empleo
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Required by the scorecard: an unknown category is routed
                  arbitrarily rather than recognised as unknown. */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sector Económico</InputLabel>
                  <Select
                    value={formData.Sector_Economico || ''}
                    onChange={handleSelectChange('Sector_Economico')}
                    label="Sector Económico"
                  >
                    {optionsFor('Sector_Economico').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Actividad del negocio</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sucursal</InputLabel>
                  <Select
                    value={formData.Sucursal_Grupo || ''}
                    onChange={handleSelectChange('Sucursal_Grupo')}
                    label="Sucursal"
                  >
                    {optionsFor('Sucursal_Grupo').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Garantía</InputLabel>
                  <Select
                    value={formData.Tipo_Garantia || ''}
                    onChange={handleSelectChange('Tipo_Garantia')}
                    label="Tipo de Garantía"
                  >
                    {(catalogues?.guarantee_types || ['PRENDARIA']).map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Mueve el score hasta 154 puntos
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Optional: the model learned a branch for their absence, so a
                  blank is honest. A declared figure is still worth having. */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ventas Anuales"
                  type="number"
                  value={formData.Importe_Venta ?? ''}
                  onChange={handleInputChange('Importe_Venta')}
                  helperText="Opcional; en blanco = no declarado"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Años de Actividad"
                  type="number"
                  value={formData.antiguedad_years ?? ''}
                  onChange={handleInputChange('antiguedad_years')}
                  helperText="Opcional; más de 80 se toma como no declarado"
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleAnalyze}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                fullWidth
                size="large"
              >
                {loading ? 'Analizando...' : 'Analizar Crédito'}
              </Button>
              <Button
                variant="outlined"
                onClick={clearForm}
                disabled={loading}
                size="large"
              >
                Limpiar
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12} lg={6}>
          <Stack spacing={2}>
            {/* Quick Metrics */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                Métricas Clave
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color={getDTIRatio() > 40 ? 'error' : 'primary'}>
                      {getDTIRatio().toFixed(1)}%
                    </Typography>
                    <Typography variant="caption">DTI Ratio</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color={getDebtServiceCoverageRatio() > 30 ? 'error' : 'primary'}>
                      {getDebtServiceCoverageRatio().toFixed(1)}%
                    </Typography>
                    <Typography variant="caption">Cobertura de Deuda</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Loan Default Prediction */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                Predicción de Riesgo de Default
              </Typography>

              {loanDefaultPrediction ? (
                <Box>
                  <Card sx={{ mb: 2, bgcolor: getRiskColor(loanDefaultPrediction.default_probability), color: 'white' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h3">
                        {formatPercentage(loanDefaultPrediction.default_probability)}
                      </Typography>
                      <Typography variant="h6">
                        Probabilidad de Default
                      </Typography>
                      <Chip
                        label={loanDefaultPrediction.risk_tier}
                        sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                    </CardContent>
                  </Card>

                  <Typography variant="body2" color="text.secondary">
                    <strong>Confianza:</strong> {formatPercentage(loanDefaultPrediction.confidence_score)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Modelo:</strong> {loanDefaultPrediction.model_version}
                  </Typography>
                  {loanDefaultPrediction.scored_at_reference_ticket && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        Cliente nuevo: esta probabilidad se calculó sobre la
                        capacidad de pago ({formatCurrency(loanDefaultPrediction.scoring_amount || 0)}),
                        no sobre el monto solicitado ({formatCurrency(loanDefaultPrediction.requested_amount || formData.Monto_Credito || 0)})
                        ni sobre el monto máximo.
                        Cambiar el monto pedido no cambia este número.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Assessment sx={{ fontSize: 60, mb: 2 }} />
                  <Typography>Ejecutar análisis para ver los resultados</Typography>
                </Box>
              )}
            </Paper>

            {/* Credit Scorecard */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                <AttachMoney sx={{ mr: 1, verticalAlign: 'middle' }} />
                Tarjeta de Puntuación Crediticia
              </Typography>

              {scorecardData ? (
                <Box>
                  <Card sx={{ mb: 2, bgcolor: getScoreColor(scorecardData.credit_score), color: 'white' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h3">
                        {scorecardData.credit_score}
                      </Typography>
                      <Typography variant="h6">
                        Puntaje Crediticio
                      </Typography>
                      <Chip
                        label={`Banda ${scorecardData.score_band}`}
                        sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                    </CardContent>
                  </Card>

                  {/* Credit Score Bands Visualization */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Bandas de Puntuación Crediticia
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      {bandRanges(
                        scorecardData.band_cutoffs ||
                          catalogues?.band_cutoffs ||
                          FALLBACK_BAND_CUTOFFS
                      ).map((bandInfo) => {
                        // Highlight the band the model assigned, not the band
                        // the score falls into by these bounds. They agree, but
                        // when they disagree the model's answer is the one that
                        // drives the decision - the legend used to be wired to
                        // its own cutoffs and contradicted the label above it.
                        const isCurrentBand = scorecardData.score_band === bandInfo.band;
                        return (
                          <Box
                            key={bandInfo.band}
                            sx={{
                              flex: 1,
                              p: 2,
                              bgcolor: isCurrentBand ? bandInfo.color : `${bandInfo.color}20`,
                              color: isCurrentBand ? 'white' : bandInfo.color,
                              borderRadius: 1,
                              border: isCurrentBand ? `2px solid ${bandInfo.color}` : `1px solid ${bandInfo.color}40`,
                              textAlign: 'center',
                              position: 'relative',
                              transform: isCurrentBand ? 'scale(1.05)' : 'scale(1)',
                              transition: 'all 0.3s ease',
                              boxShadow: isCurrentBand ? `0 4px 8px ${bandInfo.color}40` : 'none'
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {bandInfo.band}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                              {bandInfo.range}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                              {bandInfo.label}
                            </Typography>
                            {isCurrentBand && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  bgcolor: 'white',
                                  color: bandInfo.color,
                                  borderRadius: '50%',
                                  width: 24,
                                  height: 24,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  boxShadow: `0 2px 4px ${bandInfo.color}60`
                                }}
                              >
                                ✓
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                      Su puntuación: {scorecardData.credit_score} - Banda {scorecardData.score_band}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Riesgo Default:</strong> {formatPercentage(scorecardData.default_probability)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Monto Máximo:</strong> {formatCurrency(scorecardData.max_loan_amount)}
                      </Typography>
                      {scorecardData.scored_at_reference_ticket && scorecardData.scoring_amount ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Capacidad de pago {formatCurrency(scorecardData.scoring_amount)}
                          {scorecardData.score_band
                            ? ` × recorte banda ${scorecardData.score_band}`
                            : ''}
                          {' = este máximo. No es el ticket de score.'}
                        </Typography>
                      ) : null}
                    </Grid>
                  </Grid>

                  {scorecardData.max_loan_explanation && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {scorecardData.max_loan_explanation}
                    </Typography>
                  )}

                  {scorecardData.scored_at_reference_ticket && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        Cliente nuevo: el puntaje se calculó sobre la capacidad
                        de pago ({formatCurrency(scorecardData.scoring_amount || 0)}),
                        no sobre el monto solicitado ({formatCurrency(scorecardData.requested_amount || formData.Monto_Credito || 0)}).
                        El recorte por banda se aplica después: capacidad × recorte = Monto Máximo.
                        Cambiar el monto pedido no cambia el puntaje ni el máximo.
                      </Typography>
                    </Alert>
                  )}

                  {/* Anything the model could not use as supplied. Without this
                      an unrecognised category was discarded in silence, which
                      is why the score barely moved between scenarios. */}
                  {inputWarnings.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {inputWarnings.length === 1
                          ? 'Un campo no se pudo usar tal como se envió'
                          : `${inputWarnings.length} campos no se pudieron usar tal como se enviaron`}
                      </Typography>
                      <Stack spacing={0.5}>
                        {inputWarnings.map((warning, index) => (
                          <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                            • {warning}
                          </Typography>
                        ))}
                      </Stack>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <AttachMoney sx={{ fontSize: 60, mb: 2 }} />
                  <Typography>Ejecutar análisis para ver la puntuación</Typography>
                </Box>
              )}
            </Paper>

            {/* Recommendation */}
            {(loanDefaultPrediction || scorecardData) && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Recomendación Final
                </Typography>

                {loanDefaultPrediction && scorecardData && (() => {
                  const requestAmount = formData.Monto_Credito || 0;
                  const maxLoan = scorecardData.max_loan_amount || 0;
                  const requestExceedsMax = maxLoan > 0 && requestAmount > maxLoan;
                  const requestUnaffordable =
                    getDTIRatio() > 40
                    || getDebtServiceCoverageRatio() > 40
                    || requestExceedsMax;
                  const personReject = scorecardData.credit_score < 500
                    || loanDefaultPrediction.default_probability >= 0.6;
                  const personReview = scorecardData.credit_score < 600
                    || loanDefaultPrediction.default_probability >= 0.3;

                  let verdict: { severity: 'success' | 'warning' | 'error'; title: string; body: string };
                  if (personReject) {
                    verdict = {
                      severity: 'error',
                      title: 'RECHAZADO',
                      body: 'El perfil (no el monto pedido) tiene alto riesgo o un puntaje bajo.',
                    };
                  } else if (requestUnaffordable) {
                    verdict = {
                      severity: 'error',
                      title: 'NO APROBAR ESTE MONTO',
                      body: (
                        `El puntaje ${scorecardData.credit_score} es del perfil a su capacidad `
                        + `(${formatCurrency(scorecardData.scoring_amount || maxLoan)}), `
                        + `no de la solicitud de ${formatCurrency(requestAmount)}. `
                        + `El máximo recomendado es ${formatCurrency(maxLoan)}.`
                      ),
                    };
                  } else if (personReview) {
                    verdict = {
                      severity: 'warning',
                      title: 'REVISAR',
                      body: 'Requiere análisis adicional por parte del comité de crédito.',
                    };
                  } else {
                    verdict = {
                      severity: 'success',
                      title: 'APROBADO',
                      body: 'Perfil aceptable y la solicitud cabe en la capacidad de pago.',
                    };
                  }

                  return (
                    <Box>
                      <Alert severity={verdict.severity} sx={{ mb: 2 }}>
                        <strong>{verdict.title}:</strong> {verdict.body}
                      </Alert>

                      <Typography variant="body2" sx={{ mt: 2 }}>
                        • Solicitud vs. máximo: {requestExceedsMax
                          ? `❌ ${formatCurrency(requestAmount)} supera ${formatCurrency(maxLoan)}`
                          : `✅ ${formatCurrency(requestAmount)} cabe en ${formatCurrency(maxLoan)}`}
                      </Typography>
                      <Typography variant="body2">
                        • DTI Ratio: {getDTIRatio() > 40 ? '❌ Alto (>40%)' : '✅ Aceptable'}
                      </Typography>
                      <Typography variant="body2">
                        • Puntaje del perfil: {scorecardData.credit_score >= 700 ? '✅ Excelente' :
                                              scorecardData.credit_score >= 600 ? '⚠️ Bueno' : '❌ Bajo'}
                        {' '}({scorecardData.credit_score}, banda {scorecardData.score_band})
                      </Typography>
                      <Typography variant="body2">
                        • Riesgo de Default: {loanDefaultPrediction.default_probability < 0.2 ? '✅ Bajo' :
                                            loanDefaultPrediction.default_probability < 0.5 ? '⚠️ Medio' : '❌ Alto'}
                      </Typography>
                    </Box>
                  );
                })()}
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      <UnderwritingChat
        application={formData}
        scorecard={scorecardData}
        loanDefault={loanDefaultPrediction}
        analysisKey={analysisKey}
      />
    </Box>
  );
};

export default UnifiedCreditAnalysis;